<template>
  <div class="p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white">Gestión de Roles</h1>
        <p class="text-gray-600 dark:text-gray-400">Administra los roles del sistema y sus permisos</p>
      </div>
      <div class="flex items-center gap-3">
        <div class="relative group">
          <button
            @click="showExportMenu = !showExportMenu"
            class="flex items-center px-4 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg hover:from-gray-900 hover:to-black transition-all duration-300 shadow-md hover:shadow-lg"
          >
            <Icon icon="mdi:printer" class="mr-2" />
            Exportar
            <Icon icon="mdi:chevron-down" class="ml-2" />
          </button>
          <div v-if="showExportMenu"
               class="absolute right-0 mt-2 w-56 bg-gray-900 rounded-xl shadow-lg border border-gray-700 z-50">
            <div class="py-2">
              <button @click="exportToExcel" class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200">
                <Icon icon="mdi:file-excel" class="mr-3 text-green-500" />
                <div>
                  <p class="font-medium">Exportar a Excel</p>
                  <p class="text-xs text-gray-400">Formato XLSX</p>
                </div>
              </button>
              <button @click="exportToPDF" class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200">
                <Icon icon="mdi:file-pdf-box" class="mr-3 text-red-500" />
                <div>
                  <p class="font-medium">Exportar a PDF</p>
                  <p class="text-xs text-gray-400">Reporte completo</p>
                </div>
              </button>
              <div class="border-t border-gray-700 my-2"></div>
              <button @click="exportFullReport" class="w-full px-4 py-3 text-left hover:bg-gray-800 flex items-center text-gray-200">
                <Icon icon="mdi:file-document" class="mr-3 text-blue-500" />
                <div>
                  <p class="font-medium">Reporte Completo</p>
                  <p class="text-xs text-gray-400">Excel + PDF</p>
                </div>
              </button>
            </div>
          </div>
        </div>
        <button
          @click="openCreateModal"
          class="flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg"
        >
          <Icon icon="mdi:plus" class="mr-2" />
          Nuevo Rol
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-12 gap-2">
        <div class="col-span-12 md:col-span-5">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Buscar Roles</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input v-model="searchQuery" type="text" placeholder="Nombre, descripción..."
              class="w-full pl-8 pr-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs transition-all"
              @input="handleSearch" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-2">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Estado</label>
          <div class="relative">
            <select v-model="filterStatus" @change="handleFilterChange"
              class="w-full appearance-none border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 pr-7 text-xs transition-all">
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
            <Icon icon="mdi:chevron-down" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
          </div>
        </div>
        <div class="col-span-6 md:col-span-3">
          <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Ordenar Por</label>
          <div class="relative">
            <select v-model="sortBy"
              class="w-full appearance-none border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 pr-7 text-xs transition-all">
              <option value="nombre">Nombre (A-Z)</option>
              <option value="nombre_desc">Nombre (Z-A)</option>
              <option value="fechaCreacion_desc">Más recientes</option>
              <option value="fechaCreacion">Más antiguos</option>
              <option value="estado">Estado</option>
              <option value="cantidadUsuarios">Más usuarios</option>
            </select>
            <Icon icon="mdi:sort" class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-sm" />
          </div>
        </div>
        <div class="col-span-12 md:col-span-2 flex items-end">
          <button @click="loadRoles"
            class="w-full flex items-center justify-center px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-xs font-medium">
            <Icon icon="mdi:refresh" class="mr-1.5" :class="{ 'animate-spin': loading }" />
            {{ loading ? 'Cargando...' : 'Actualizar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <div class="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-5 rounded-xl border border-blue-200 dark:border-blue-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-md">
            <Icon icon="mdi:account-group" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Total Roles</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ roles.length }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded-full">
                {{ filteredRoles.length }} filtrados
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-5 rounded-xl border border-green-200 dark:border-green-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-md">
            <Icon icon="mdi:check-circle" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Roles Activos</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estadisticas.activos }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 rounded-full">
                {{ estadisticas.porcentajeActivos }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 p-5 rounded-xl border border-red-200 dark:border-red-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-md">
            <Icon icon="mdi:close-circle" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Roles Inactivos</p>
            <div class="flex items-baseline">
              <p class="text-3xl font-bold text-gray-800 dark:text-white">{{ estadisticas.inactivos }}</p>
              <span class="ml-2 text-xs px-2 py-1 bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 rounded-full">
                {{ estadisticas.porcentajeInactivos }}%
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-5 rounded-xl border border-purple-200 dark:border-purple-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-md">
            <Icon icon="mdi:shield-account" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Con Permisos</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ estadisticas.conPermisos }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {{ estadisticas.porcentajeConPermisos }}% del total
            </p>
          </div>
        </div>
      </div>
      <div class="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 p-5 rounded-xl border border-amber-200 dark:border-amber-700 shadow-sm">
        <div class="flex items-center">
          <div class="p-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg shadow-md">
            <Icon icon="mdi:account-multiple" class="text-white text-2xl" />
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-600 dark:text-gray-400 font-medium">Usuarios Asignados</p>
            <p class="text-2xl font-bold text-gray-800 dark:text-white">{{ estadisticas.totalUsuarios }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              promedio {{ estadisticas.promedioUsuarios }} por rol
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Indicadores de filtro -->
    <div v-if="isFiltered" class="mb-4">
      <div class="flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
        <span class="text-sm font-medium text-amber-800 dark:text-amber-300">
          <Icon icon="mdi:filter" class="inline mr-1" />
          Filtros aplicados:
        </span>
        <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
          Buscar: "{{ searchQuery }}"
          <button @click="searchQuery = ''; handleSearch()" class="ml-1.5 hover:text-blue-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <span v-if="filterStatus !== 'all'" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
          Estado: {{ filterStatus === 'active' ? 'Activos' : 'Inactivos' }}
          <button @click="filterStatus = 'all'; handleFilterChange()" class="ml-1.5 hover:text-indigo-900">
            <Icon icon="mdi:close" class="text-xs" />
          </button>
        </span>
        <button
          @click="clearFilters"
          class="text-sm text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300 font-medium"
        >
          <Icon icon="mdi:filter-off" class="inline mr-1" />
          Limpiar filtros
        </button>
      </div>
    </div>

    <!-- Tabla de roles -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-white">Lista de Roles</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Mostrando {{ filteredRoles.length }} de {{ roles.length }} roles
          </p>
        </div>
        <div class="flex items-center space-x-2">
          <div class="text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:calendar" class="inline mr-1" />
            Última actualización: {{ formatTime(lastUpdate) }}
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                <div class="flex items-center space-x-1">
                  <span>Rol</span>
                  <button @click="sortBy = sortBy === 'nombre' ? 'nombre_desc' : 'nombre'">
                    <Icon icon="mdi:sort-alphabetical" class="text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Descripción</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Permisos</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Usuarios</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Fecha Registro</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="loading">
              <td colspan="7" class="px-6 py-12">
                <div class="flex flex-col items-center justify-center">
                  <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mb-4"></div>
                  <p class="text-gray-600 dark:text-gray-400">Cargando roles...</p>
                </div>
              </td>
            </tr>
            <tr v-else-if="filteredRoles.length === 0">
              <td colspan="7" class="px-6 py-16 text-center">
                <div class="flex flex-col items-center">
                  <div class="p-4 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
                    <Icon icon="mdi:account-remove" class="text-3xl text-gray-400" />
                  </div>
                  <h3 class="text-lg font-medium text-gray-800 dark:text-white mb-2">No se encontraron roles</h3>
                  <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
                    {{ searchQuery || filterStatus !== 'all'
                      ? 'Intenta con otros términos de búsqueda o cambia los filtros.'
                      : 'No hay roles registrados. ¡Crea el primero!' }}
                  </p>
                  <button
                    v-if="!searchQuery && filterStatus === 'all'"
                    @click="openCreateModal"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Icon icon="mdi:plus" class="mr-2 inline" />
                    Crear primer rol
                  </button>
                  <button
                    v-else
                    @click="clearFilters"
                    class="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Icon icon="mdi:filter-off" class="mr-2 inline" />
                    Limpiar filtros
                  </button>
                </div>
              </td>
            </tr>
            <tr
              v-else
              v-for="rol in paginatedRoles"
              :key="rol.idRol"
              class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 cursor-pointer"
              @click="openDetailModal(rol)"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-12 w-12">
                    <div :class="[
                      'h-12 w-12 rounded-xl flex items-center justify-center shadow-sm',
                      rol.estado?.toLowerCase() === 'activo'
                        ? 'bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-900 dark:to-indigo-900'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900'
                    ]">
                      <Icon icon="mdi:account" :class="[
                        'text-xl',
                        rol.estado?.toLowerCase() === 'activo'
                          ? 'text-purple-600 dark:text-purple-400'
                          : 'text-gray-500 dark:text-gray-400'
                      ]" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-semibold text-gray-900 dark:text-white">
                      {{ rol.nombre }}
                      <span v-if="rol.estado?.toLowerCase() !== 'activo'" class="ml-2 text-xs text-gray-500">
                        (Inactivo)
                      </span>
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-1">
                      <Icon icon="mdi:identifier" class="mr-1" />
                      ID: {{ rol.idRol }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <p class="text-sm text-gray-900 dark:text-white max-w-md line-clamp-2">
                  {{ rol.descripcion || 'Sin descripción' }}
                </p>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <Icon icon="mdi:shield" class="text-gray-400 mr-2" />
                  <span class="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                    {{ rol.cantidadPermisos || 0 }}
                  </span>
                  <span class="text-xs text-gray-500 ml-1">permisos</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <Icon icon="mdi:account-multiple" class="text-gray-400 mr-2" />
                  <span class="text-sm font-semibold text-green-600 dark:text-green-400">
                    {{ rol.cantidadUsuarios || 0 }}
                  </span>
                  <span class="text-xs text-gray-500 ml-1">usuarios</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-white">
                  {{ formatDate(rol.fecha_creacion || rol.fechaCreacion) }}
                </div>
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  {{ formatTime(rol.fecha_creacion || rol.fechaCreacion) }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  @click.stop="toggleStatus(rol)"
                  :class="[
                    'px-3 py-1.5 inline-flex text-xs leading-4 font-semibold rounded-full shadow-sm transition-all duration-300 cursor-pointer',
                    rol.estado?.toLowerCase() === 'activo'
                      ? 'bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900 dark:to-emerald-900 text-green-800 dark:text-green-200 hover:from-green-200 hover:to-emerald-200 dark:hover:from-green-800 dark:hover:to-emerald-800'
                      : 'bg-gradient-to-r from-red-100 to-pink-100 dark:from-red-900 dark:to-pink-900 text-red-800 dark:text-red-200 hover:from-red-200 hover:to-pink-200 dark:hover:from-red-800 dark:hover:to-pink-800'
                  ]"
                >
                  <Icon :icon="rol.estado?.toLowerCase() === 'activo' ? 'mdi:check-circle' : 'mdi:close-circle'" class="mr-1.5" />
                  {{ convertirEstadoATexto(rol.estado) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex items-center space-x-2">
                  <button
                    @click.stop="openEditModal(rol)"
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-800/50 transition-colors"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" />
                  </button>
                  <button
                    @click.stop="openAssignPermissionsModal(rol)"
                    class="p-2 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-800/50 transition-colors"
                    title="Asignar permisos"
                  >
                    <Icon icon="mdi:shield-plus" />
                  </button>
                  <button
                    @click.stop="toggleStatus(rol)"
                    :class="[
                      'p-2 rounded-lg transition-colors',
                      rol.estado?.toLowerCase() === 'activo'
                        ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/50'
                        : 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/50'
                    ]"
                    :title="rol.estado?.toLowerCase() === 'activo' ? 'Desactivar' : 'Activar'"
                  >
                    <Icon :icon="rol.estado?.toLowerCase() === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" />
                  </button>
                  <button
                    @click.stop="confirmDelete(rol)"
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-800/50 transition-colors"
                    title="Eliminar"
                    :disabled="rol.cantidadUsuarios > 0"
                  >
                    <Icon icon="mdi:delete" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="filteredRoles.length > 0 && !loading" class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-xs text-gray-500 dark:text-gray-400">
          Mostrando <span class="font-semibold text-gray-700 dark:text-gray-300">{{ startItem }}–{{ endItem }}</span> de <span class="font-semibold text-gray-700 dark:text-gray-300">{{ filteredRoles.length }}</span> resultados
        </div>
        <div class="flex items-center gap-1">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            <Icon icon="mdi:chevron-left" class="text-base" />
          </button>
          <button v-for="page in visiblePages" :key="page" @click="currentPage = page"
            :class="['w-8 h-8 flex items-center justify-center rounded-lg text-xs font-semibold transition-all',
              currentPage === page
                ? 'bg-blue-600 text-white shadow-sm shadow-blue-300 dark:shadow-blue-900'
                : 'border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700']">{{ page }}</button>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
            class="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all">
            <Icon icon="mdi:chevron-right" class="text-base" />
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MODAL CREAR / EDITAR ROL                                     -->
    <!-- ============================================================ -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg transform transition-all duration-300 scale-100">
          <div class="px-8 pt-8 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 shadow-md mr-4">
                  <Icon :icon="editingRol ? 'mdi:pencil-box' : 'mdi:account-plus'" class="text-white text-2xl" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                    {{ editingRol ? 'Editar Rol' : 'Nuevo Rol' }}
                  </h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {{ editingRol ? 'Modifica los datos del rol' : 'Completa los datos para crear un nuevo rol' }}
                  </p>
                </div>
              </div>
              <button @click="closeModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500">
                <Icon icon="mdi:close" class="text-xl" />
              </button>
            </div>
          </div>
          <form @submit.prevent="saveRol" class="px-8 py-6 max-h-[80vh] overflow-y-auto">
            <div class="space-y-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nombre del rol *</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon icon="mdi:tag" class="text-gray-400" />
                  </div>
                  <input v-model="form.nombre" type="text" required maxlength="50"
                    class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300"
                    placeholder="Ej: Administrador, Vendedor, Técnico..."
                    :class="{ 'border-red-500': errors.nombre }" />
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <span class="text-xs text-gray-500">{{ form.nombre.length }}/50</span>
                  </div>
                </div>
                <p v-if="errors.nombre" class="mt-2 text-sm text-red-600 dark:text-red-400">{{ errors.nombre }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descripción</label>
                <div class="relative">
                  <div class="absolute top-3 left-3 pointer-events-none">
                    <Icon icon="mdi:text-box" class="text-gray-400" />
                  </div>
                  <textarea v-model="form.descripcion" rows="3" maxlength="500"
                    class="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all duration-300 resize-none"
                    placeholder="Describe las responsabilidades de este rol..."></textarea>
                  <div class="absolute bottom-3 right-3">
                    <span class="text-xs text-gray-500">{{ form.descripcion.length }}/500</span>
                  </div>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Estado</label>
                <div class="grid grid-cols-2 gap-3">
                  <label class="flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                    :class="form.estado === 'activo' ? 'border-green-500 bg-green-50 dark:bg-green-900/30' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                    <input type="radio" v-model="form.estado" value="activo" class="sr-only">
                    <div class="flex items-center w-full">
                      <div class="h-6 w-6 rounded-full border-2 flex items-center justify-center mr-3"
                        :class="form.estado === 'activo' ? 'border-green-500 bg-green-500' : 'border-gray-400'">
                        <Icon v-if="form.estado === 'activo'" icon="mdi:check" class="text-white text-xs" />
                      </div>
                      <div>
                        <p class="font-medium text-gray-900 dark:text-white">Activo</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">Disponible</p>
                      </div>
                    </div>
                  </label>
                  <label class="flex items-center p-3 border-2 rounded-xl cursor-pointer transition-all duration-300"
                    :class="form.estado === 'inactivo' ? 'border-red-500 bg-red-50 dark:bg-red-900/30' : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400'">
                    <input type="radio" v-model="form.estado" value="inactivo" class="sr-only">
                    <div class="flex items-center w-full">
                      <div class="h-6 w-6 rounded-full border-2 flex items-center justify-center mr-3"
                        :class="form.estado === 'inactivo' ? 'border-red-500 bg-red-500' : 'border-gray-400'">
                        <Icon v-if="form.estado === 'inactivo'" icon="mdi:check" class="text-white text-xs" />
                      </div>
                      <div>
                        <p class="font-medium text-gray-900 dark:text-white">Inactivo</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">No disponible</p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div class="mt-8 flex justify-end space-x-3">
              <button type="button" @click="closeModal"
                class="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 hover:shadow-md">
                Cancelar
              </button>
              <button type="submit" :disabled="saving || !isFormValid"
                :class="[
                  'px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center',
                  isFormValid && !saving
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg'
                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                ]">
                <span v-if="saving" class="flex items-center">
                  <Icon icon="mdi:loading" class="animate-spin mr-2" />
                  Guardando...
                </span>
                <span v-else class="flex items-center">
                  <Icon :icon="editingRol ? 'mdi:content-save' : 'mdi:plus-circle'" class="mr-2" />
                  {{ editingRol ? 'Actualizar' : 'Crear' }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- ============================================================ -->
    <!-- ⭐ MODAL ASIGNAR PERMISOS — VISTA VISUAL DETALLADA POR MÓDULO -->
    <!-- ============================================================ -->
    <Teleport to="body">
      <div v-if="showAssignPermissionsModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-2 sm:p-4 z-[90] backdrop-blur-[2px]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">

          <!-- HEADER -->
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gradient-to-r from-purple-600 to-indigo-600 rounded-t-2xl">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <Icon icon="mdi:shield-account" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-lg font-bold text-white">Permisos Visuales por Módulo</h3>
                <p class="text-xs text-purple-100">
                  Rol: <strong>{{ selectedRol?.nombre }}</strong>
                  · Marca los módulos/submódulos que podrá ver
                </p>
              </div>
            </div>
            <button @click="closeAssignPermissionsModal" class="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>

          <!-- BODY -->
          <div class="flex-1 overflow-y-auto p-4 sm:p-6">
            <div v-if="loadingPermisos" class="flex justify-center py-12">
              <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-600"></div>
            </div>
            <div v-else class="space-y-4">

              <div class="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl border border-blue-200 dark:border-blue-800">
                <p class="text-xs text-blue-800 dark:text-blue-300 flex items-center gap-2">
                  <Icon icon="mdi:information" class="text-base" />
                  Marca los <strong>permisos</strong> que este rol podrá usar.
                  Cada módulo puede tener varios permisos: ver, crear, editar, eliminar, etc.
                </p>
              </div>

              <!-- ÁRBOL DE MÓDULOS CON PERMISOS DETALLADOS -->
              <div v-for="padre in modulosConPermisos" :key="padre.idModulo"
                   class="border-2 rounded-2xl overflow-hidden transition-all"
                   :class="{
                     'border-purple-400 dark:border-purple-600 bg-purple-50/30 dark:bg-purple-900/10': isPadreCompleto(padre),
                     'border-amber-400 dark:border-amber-600 bg-amber-50/30 dark:bg-amber-900/10': isPadreParcial(padre),
                     'border-gray-200 dark:border-gray-700': !isPadreCompleto(padre) && !isPadreParcial(padre)
                   }">

                <!-- CABECERA PADRE -->
                <div class="px-4 py-3 flex items-center justify-between flex-wrap gap-2"
                     :class="{
                       'bg-purple-100 dark:bg-purple-900/40': isPadreCompleto(padre),
                       'bg-amber-100 dark:bg-amber-900/40': isPadreParcial(padre),
                       'bg-gray-50 dark:bg-gray-700/50': !isPadreCompleto(padre) && !isPadreParcial(padre)
                     }">
                  <div class="flex items-center gap-3 flex-1 min-w-0">
                    <input type="checkbox"
                      :checked="isPadreCompleto(padre)"
                      :indeterminate.prop="isPadreParcial(padre)"
                      @change="togglePadre(padre, $event)"
                      class="w-5 h-5 rounded cursor-pointer text-purple-600 focus:ring-purple-500" />

                    <div class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                         :class="isPadreCompleto(padre) ? 'bg-purple-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'">
                      <Icon :icon="padre.icono || 'mdi:folder'" class="text-lg" />
                    </div>

                    <div class="min-w-0">
                      <h4 class="font-bold text-sm text-gray-900 dark:text-white truncate">
                        {{ padre.nombre }}
                      </h4>
                      <p class="text-[10px] text-gray-500 dark:text-gray-400">
                        {{ padre.hijos.length }} submódulos · {{ padre.totalPermisos }} permisos
                      </p>
                    </div>
                  </div>

                  <span class="px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide"
                        :class="{
                          'bg-purple-500 text-white': isPadreCompleto(padre),
                          'bg-amber-500 text-white': isPadreParcial(padre),
                          'bg-gray-200 dark:bg-gray-700 text-gray-500': !isPadreCompleto(padre) && !isPadreParcial(padre)
                        }">
                    {{ isPadreCompleto(padre) ? '✓ Total' :
                       isPadreParcial(padre) ? '◐ Parcial' : '✗ Sin acceso' }}
                  </span>
                </div>

                <!-- PERMISOS DIRECTOS DEL PADRE -->
                <div v-if="padre.permisosPadre && padre.permisosPadre.length > 0"
                     class="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                  <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5">
                    Permisos del módulo
                  </p>
                  <div class="flex flex-wrap gap-1.5">
                    <label
                      v-for="p in padre.permisosPadre"
                      :key="p.idPermiso"
                      class="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg cursor-pointer text-xs font-bold transition-all border-2"
                      :class="permisosSeleccionados.includes(p.idPermiso)
                        ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-400 text-purple-700 dark:text-purple-300'
                        : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-purple-300'">
                      <input type="checkbox" :value="p.idPermiso" v-model="permisosSeleccionados"
                             class="w-3.5 h-3.5 rounded text-purple-600 focus:ring-purple-500" />
                      <Icon :icon="obtenerIconoPorAccion(p.nombre_accion)" class="text-xs" />
                      {{ formatearNombreAccion(p.nombre_accion) }}
                    </label>
                  </div>
                </div>

                <!-- HIJOS CON TODOS SUS PERMISOS -->
                <div v-if="padre.hijos && padre.hijos.length > 0"
                     class="divide-y divide-gray-100 dark:divide-gray-700">
                  <div v-for="hijo in padre.hijos" :key="hijo.idModulo"
                       class="px-3 py-2.5"
                       :class="isHijoCompleto(hijo) ? 'bg-purple-50/30 dark:bg-purple-900/10' : ''">

                    <!-- HEADER DEL HIJO -->
                    <div class="flex items-center gap-2.5 mb-2">
                      <input type="checkbox"
                        :checked="isHijoCompleto(hijo)"
                        :indeterminate.prop="isHijoParcial(hijo)"
                        @change="toggleHijo(hijo, $event)"
                        class="w-4 h-4 rounded cursor-pointer text-purple-600 focus:ring-purple-500" />

                      <div class="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                           :class="isHijoCompleto(hijo) ? 'bg-purple-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'">
                        <Icon :icon="hijo.icono || 'mdi:circle-small'" class="text-xs" />
                      </div>

                      <div class="flex-1 min-w-0">
                        <p class="text-xs font-bold text-gray-900 dark:text-white truncate">
                          {{ hijo.nombre }}
                        </p>
                        <p class="text-[10px] text-gray-400 dark:text-gray-500 truncate">
                          {{ hijo.ruta || 'Sin ruta' }}
                        </p>
                      </div>

                      <span class="text-[10px] font-black text-gray-400">
                        {{ hijo.permisos.filter(p => permisosSeleccionados.includes(p.idPermiso)).length }}/{{ hijo.permisos.length }}
                      </span>
                    </div>

                    <!-- PERMISOS DEL HIJO -->
                    <div class="flex flex-wrap gap-1.5 ml-6">
                      <label
                        v-for="p in hijo.permisos"
                        :key="p.idPermiso"
                        class="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg cursor-pointer text-[11px] font-bold transition-all border-2"
                        :class="permisosSeleccionados.includes(p.idPermiso)
                          ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-400 text-purple-700 dark:text-purple-300'
                          : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-purple-300'">
                        <input type="checkbox" :value="p.idPermiso" v-model="permisosSeleccionados"
                               class="w-3.5 h-3.5 rounded text-purple-600 focus:ring-purple-500" />
                        <Icon :icon="obtenerIconoPorAccion(p.nombre_accion)" class="text-xs" />
                        {{ formatearNombreAccion(p.nombre_accion) }}
                      </label>
                    </div>
                  </div>
                </div>

                <div v-if="(!padre.hijos || padre.hijos.length === 0) && (!padre.permisosPadre || padre.permisosPadre.length === 0)"
                     class="px-4 py-3 text-center text-xs text-gray-400 italic">
                  Este módulo no tiene permisos configurados
                </div>
              </div>

              <div v-if="modulosConPermisos.length === 0" class="text-center py-12">
                <Icon icon="mdi:folder-off" class="text-5xl text-gray-300 mx-auto mb-2" />
                <p class="text-sm text-gray-500">No hay módulos con permisos configurados</p>
              </div>
            </div>
          </div>

          <!-- FOOTER -->
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-2xl flex flex-col sm:flex-row justify-between items-center gap-3">
            <div class="flex items-center gap-4 text-sm">
              <div class="flex items-center gap-2">
                <Icon icon="mdi:shield-check" class="text-purple-600 text-lg" />
                <span class="font-black text-gray-900 dark:text-white">{{ permisosSeleccionados.length }}</span>
                <span class="text-gray-500 dark:text-gray-400">permisos</span>
              </div>
              <div class="hidden sm:flex items-center gap-2 text-xs">
                <span class="w-3 h-3 rounded-full bg-purple-500"></span>
                <span class="text-gray-500">Total</span>
                <span class="w-3 h-3 rounded-full bg-amber-500 ml-2"></span>
                <span class="text-gray-500">Parcial</span>
                <span class="w-3 h-3 rounded-full bg-gray-300 ml-2"></span>
                <span class="text-gray-500">Sin acceso</span>
              </div>
            </div>
            <div class="flex gap-2 w-full sm:w-auto">
              <button @click="deseleccionarTodo"
                class="flex-1 sm:flex-none px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 font-bold text-xs hover:bg-gray-100 dark:hover:bg-gray-800 transition-all">
                <Icon icon="mdi:close-circle-outline" class="inline mr-1" />
                Limpiar
              </button>
              <button @click="seleccionarTodo"
                class="flex-1 sm:flex-none px-4 py-2.5 border-2 border-purple-300 dark:border-purple-700 rounded-xl text-purple-700 dark:text-purple-300 font-bold text-xs hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all">
                <Icon icon="mdi:check-all" class="inline mr-1" />
                Todo
              </button>
              <button @click="savePermissionsAssignment" :disabled="savingAssignment"
                class="flex-1 sm:flex-none px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-xs hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50">
                <Icon v-if="savingAssignment" icon="mdi:loading" class="animate-spin inline mr-1" />
                <Icon v-else icon="mdi:content-save" class="inline mr-1" />
                {{ savingAssignment ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </Teleport>

    <!-- Modal Detalles -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[90] backdrop-blur-[2px]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
          <div class="p-8">
            <div class="flex justify-between items-start mb-6">
              <div>
                <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">Detalles del Rol</h3>
                <p class="text-sm text-gray-600 dark:text-gray-400">Información completa del rol</p>
              </div>
              <button @click="closeDetailModal" class="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <Icon icon="mdi:close" class="text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div v-if="selectedRol" class="space-y-6">
              <div class="flex items-center space-x-4">
                <div class="p-4 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500">
                  <Icon icon="mdi:account" class="text-white text-2xl" />
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900 dark:text-white">{{ selectedRol.nombre }}</h4>
                  <p class="text-sm text-gray-500 dark:text-gray-400">ID: {{ selectedRol.idRol }}</p>
                </div>
              </div>
              <div class="space-y-4">
                <div>
                  <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Descripción</label>
                  <p class="mt-1 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                    {{ selectedRol.descripcion || 'Sin descripción' }}
                  </p>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</label>
                    <p :class="[
                      'mt-1 px-3 py-1.5 rounded-full inline-block',
                      selectedRol.estado?.toLowerCase() === 'activo'
                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                        : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    ]">
                      {{ convertirEstadoATexto(selectedRol.estado) }}
                    </p>
                  </div>
                  <div>
                    <label class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Permisos</label>
                    <button @click="verPermisosDelRol(selectedRol.idRol)"
                      class="mt-1 px-3 py-1.5 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 rounded-full text-sm hover:bg-indigo-200 transition-colors">
                      <Icon icon="mdi:shield" class="inline mr-1" />
                      {{ selectedRol.cantidadPermisos || 0 }} permisos
                    </button>
                  </div>
                </div>
                <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div class="flex justify-end space-x-3">
                    <button @click="openEditModal(selectedRol); closeDetailModal()"
                      class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                      Editar
                    </button>
                    <button @click="openAssignPermissionsModal(selectedRol); closeDetailModal()"
                      class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                      Asignar Permisos
                    </button>
                    <button @click="closeDetailModal"
                      class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de Confirmación -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[100] backdrop-blur-[2px]">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100">
          <div class="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div class="flex items-center space-x-3">
              <div :class="[
                'p-2 rounded-full',
                confirmConfig.type === 'danger' ? 'bg-red-100 dark:bg-red-900/30' :
                confirmConfig.type === 'warning' ? 'bg-amber-100 dark:bg-amber-900/30' :
                'bg-blue-100 dark:bg-blue-900/30'
              ]">
                <Icon
                  :icon="confirmConfig.type === 'danger' ? 'mdi:alert-octagon' :
                         confirmConfig.type === 'warning' ? 'mdi:alert' :
                         'mdi:information'"
                  :class="[
                    'text-2xl',
                    confirmConfig.type === 'danger' ? 'text-red-600 dark:text-red-400' :
                    confirmConfig.type === 'warning' ? 'text-amber-600 dark:text-amber-400' :
                    'text-blue-600 dark:text-blue-400'
                  ]" />
              </div>
              <h3 class="text-xl font-bold text-gray-900 dark:text-white">{{ confirmConfig.title }}</h3>
            </div>
          </div>
          <div class="px-6 py-4">
            <p class="text-gray-700 dark:text-gray-300" v-html="confirmConfig.message"></p>
            <p v-if="confirmConfig.detail" class="text-sm text-gray-500 dark:text-gray-400 mt-2">{{ confirmConfig.detail }}</p>
          </div>
          <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <button @click="cancelConfirm"
              class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              {{ confirmConfig.cancelText }}
            </button>
            <button @click="executeConfirm"
              :class="[
                'px-4 py-2 rounded-lg text-white font-medium transition-colors',
                confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                confirmConfig.type === 'warning' ? 'bg-amber-600 hover:bg-amber-700' :
                'bg-blue-600 hover:bg-blue-700'
              ]">
              {{ confirmConfig.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import { useToast } from "vue-toastification";

import {
  obtenerRoles,
  obtenerRolPorId,
  verificarNombreRol,
  crearRol,
  actualizarRol,
  eliminarRol,
  cambiarEstadoRol,
  convertirEstadoATexto,
  buscarRoles,
  filtrarRolesPorEstado,
  obtenerEstadisticasRoles,
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto
} from '@/api/roles.js';

import {
  obtenerPermisos,
  asignarPermisoARol,
  quitarPermisoDeRol,
  obtenerPermisosPorRolConEstado,
  formatearNombreAccion,
  obtenerIconoPorAccion
} from '@/api/permiso.js';

import { obtenerModulos } from '@/api/modulo.js';

export default {
  name: 'RolesList',
  components: { Icon },
  data() {
    return {
      roles: [],
      permisosDisponibles: [],
      modulosDisponibles: [],
      loading: false,
      saving: false,
      savingAssignment: false,
      loadingPermisos: false,
      showModal: false,
      showDetailModal: false,
      showAssignPermissionsModal: false,
      showExportMenu: false,
      showConfirmModal: false,
      confirmConfig: {
        title: 'Confirmar', message: '', detail: '', confirmText: 'Aceptar',
        cancelText: 'Cancelar', type: 'info', onConfirm: null, onCancel: null
      },
      searchQuery: '',
      filterStatus: 'all',
      sortBy: 'nombre',
      currentPage: 1,
      itemsPerPage: 10,
      form: { idRol: null, nombre: '', descripcion: '', estado: 'activo' },
      errors: {},
      editingRol: null,
      selectedRol: null,
      selectedRolId: null,
      permisosSeleccionados: [],
      lastUpdate: new Date(),
    };
  },
  computed: {
    filteredRoles() {
      let filtered = [...this.roles];
      if (this.searchQuery.trim()) filtered = buscarRoles(filtered, this.searchQuery);
      filtered = filtrarRolesPorEstado(filtered, this.filterStatus);
      if (this.sortBy === 'nombre') filtered.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
      else if (this.sortBy === 'nombre_desc') filtered.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
      else if (this.sortBy === 'fechaCreacion_desc') filtered.sort((a, b) => new Date(b.fecha_creacion || b.fechaCreacion) - new Date(a.fecha_creacion || a.fechaCreacion));
      else if (this.sortBy === 'fechaCreacion') filtered.sort((a, b) => new Date(a.fecha_creacion || a.fechaCreacion) - new Date(b.fecha_creacion || b.fechaCreacion));
      else if (this.sortBy === 'estado') filtered.sort((a, b) => (a.estado || '').localeCompare(b.estado || ''));
      else if (this.sortBy === 'cantidadUsuarios') filtered.sort((a, b) => (b.cantidadUsuarios || 0) - (a.cantidadUsuarios || 0));
      return filtered;
    },

    estadisticas() {
      const baseStats = obtenerEstadisticasRoles(this.roles);
      const conPermisos = this.roles.filter(r => (r.cantidadPermisos || 0) > 0).length;
      const totalUsuarios = this.roles.reduce((sum, r) => sum + (r.cantidadUsuarios || 0), 0);
      const promedioUsuarios = this.roles.length > 0 ? (totalUsuarios / this.roles.length).toFixed(1) : 0;
      return {
        ...baseStats, conPermisos,
        porcentajeConPermisos: this.roles.length > 0 ? Math.round((conPermisos / this.roles.length) * 100) : 0,
        totalUsuarios, promedioUsuarios
      };
    },

    /**
     * ⭐ AGRUPACIÓN VISUAL: Módulos padre → hijos → permisos
     */
    modulosConPermisos() {
      if (!this.permisosDisponibles?.length) return [];

      const permisosPorModulo = {};
      this.permisosDisponibles.forEach(p => {
        if (!permisosPorModulo[p.idModulo]) permisosPorModulo[p.idModulo] = [];
        permisosPorModulo[p.idModulo].push(p);
      });

      const modulosConInfo = this.modulosDisponibles?.length
        ? this.modulosDisponibles
        : Object.keys(permisosPorModulo).map(id => ({
            idModulo: parseInt(id), idPadre: null,
            nombre: permisosPorModulo[id][0]?.nombreModulo || 'Módulo',
            icono: permisosPorModulo[id][0]?.iconoModulo || 'mdi:folder',
            ruta: permisosPorModulo[id][0]?.rutaModulo,
          }));

      const padres = modulosConInfo.filter(m => !m.idPadre);

      return padres.map(padre => {
        const hijosDelPadre = modulosConInfo.filter(m => m.idPadre === padre.idModulo);

        const hijos = hijosDelPadre.map(hijo => {
          const permisos = permisosPorModulo[hijo.idModulo] || [];
          if (permisos.length === 0) return null;
          return {
            idModulo: hijo.idModulo,
            nombre: hijo.nombre,
            icono: hijo.icono,
            ruta: hijo.ruta,
            permisos: permisos.map(p => ({
              idPermiso: p.idPermiso,
              nombre_accion: p.nombre_accion,
              descripcion: p.descripcion
            }))
          };
        }).filter(Boolean);

        const permisosPadre = (permisosPorModulo[padre.idModulo] || []).map(p => ({
          idPermiso: p.idPermiso,
          nombre_accion: p.nombre_accion,
          descripcion: p.descripcion
        }));

        const totalPermisos = hijos.reduce((sum, h) => sum + h.permisos.length, 0) + permisosPadre.length;

        return {
          idModulo: padre.idModulo, nombre: padre.nombre, icono: padre.icono,
          idPadre: padre.idPadre, permisosPadre, hijos, totalPermisos
        };
      }).filter(p => p.hijos.length > 0 || p.permisosPadre.length > 0);
    },

    paginatedRoles() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredRoles.slice(start, start + this.itemsPerPage);
    },
    totalPages() { return Math.ceil(this.filteredRoles.length / this.itemsPerPage); },
    visiblePages() {
      const pages = [], max = 5;
      let start = Math.max(1, this.currentPage - Math.floor(max / 2));
      let end = Math.min(this.totalPages, start + max - 1);
      if (end - start + 1 < max) start = Math.max(1, end - max + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    startItem() { return (this.currentPage - 1) * this.itemsPerPage + 1; },
    endItem() { return Math.min(this.currentPage * this.itemsPerPage, this.filteredRoles.length); },
    isFormValid() { return this.form.nombre && this.form.nombre.trim().length > 0; },
    isFiltered() { return this.searchQuery.trim() || this.filterStatus !== 'all'; },
  },
  setup() {
    const toast = useToast();
    return { toast, convertirEstadoATexto, formatearNombreAccion, obtenerIconoPorAccion };
  },
  mounted() {
    this.loadRoles();
    this.loadPermisos();
    this.loadModulos();
  },
  watch: {
    filterStatus() { this.currentPage = 1; },
    searchQuery() { this.currentPage = 1; },
    sortBy() { this.currentPage = 1; },
  },
  methods: {
    showConfirm(options) {
      return new Promise((resolve) => {
        this.confirmConfig = {
          title: options.title || 'Confirmar',
          message: options.message || '¿Estás seguro?',
          detail: options.detail || '',
          confirmText: options.confirmText || 'Aceptar',
          cancelText: options.cancelText || 'Cancelar',
          type: options.type || 'info',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        };
        this.showConfirmModal = true;
      });
    },
    cancelConfirm() { this.showConfirmModal = false; if (this.confirmConfig.onCancel) this.confirmConfig.onCancel(); },
    executeConfirm() { this.showConfirmModal = false; if (this.confirmConfig.onConfirm) this.confirmConfig.onConfirm(); },

    async loadRoles() {
      this.loading = true;
      try {
        let estado = this.filterStatus;
        if (estado === 'active') estado = 'activos';
        else if (estado === 'inactive') estado = 'inactivos';
        else estado = 'all';
        const response = await obtenerRoles(estado);
        if (response.success) {
          this.roles = response.data || [];
          this.lastUpdate = new Date();
        } else throw new Error(response.error || 'Error al cargar roles');
      } catch (error) {
        console.error('❌ Error al cargar roles:', error);
        this.toast.error('Error al cargar los roles: ' + error.message);
      } finally { this.loading = false; }
    },

    async loadPermisos() {
      try {
        const response = await obtenerPermisos();
        if (response.success) this.permisosDisponibles = response.data || [];
      } catch (error) { console.error('❌ Error cargando permisos:', error); }
    },

    async loadModulos() {
      try {
        const response = await obtenerModulos('activos');
        if (response.success) {
          this.modulosDisponibles = (response.data || []).map(m => ({
            idModulo: m.idModulo ?? m.id,
            idPadre: m.idPadre ?? m.id_padre ?? null,
            nombre: m.nombre || '',
            icono: m.icono || 'mdi:folder-outline',
            ruta: m.ruta || '',
            tipo: m.tipo || 'modulo',
            orden: m.orden ?? 0
          }));
        }
      } catch (error) { console.error('❌ Error cargando módulos:', error); }
    },

    handleFilterChange() { this.currentPage = 1; this.loadRoles(); },
    handleSearch: debounce(function() { this.currentPage = 1; }, 300),

    openCreateModal() { this.editingRol = null; this.resetForm(); this.errors = {}; this.showModal = true; },
    openEditModal(rol) {
      this.editingRol = rol;
      this.form = { idRol: rol.idRol, nombre: rol.nombre, descripcion: rol.descripcion || '', estado: rol.estado || 'activo' };
      this.errors = {}; this.showModal = true;
    },
    openDetailModal(rol) { this.selectedRol = rol; this.showDetailModal = true; },

    async openAssignPermissionsModal(rol) {
      this.selectedRol = rol;
      this.selectedRolId = rol.idRol;
      this.permisosSeleccionados = [];
      this.loadingPermisos = true;
      this.showAssignPermissionsModal = true;
      try {
        const response = await obtenerPermisosPorRolConEstado(rol.idRol);
        if (response.success) {
          this.permisosSeleccionados = (response.data || [])
            .filter(p => p.asignado)
            .map(p => p.idPermiso);
        }
      } catch (error) {
        console.error('❌ Error cargando permisos asignados:', error);
      } finally { this.loadingPermisos = false; }
    },

    // ⭐ Métodos visuales para el padre
    _getTodosPermisosDePadre(padre) {
      const ids = [];
      if (padre.permisosPadre) padre.permisosPadre.forEach(p => ids.push(p.idPermiso));
      if (padre.hijos) padre.hijos.forEach(h => h.permisos.forEach(p => ids.push(p.idPermiso)));
      return ids;
    },

    isPadreCompleto(padre) {
      const todos = this._getTodosPermisosDePadre(padre);
      if (todos.length === 0) return false;
      return todos.every(id => this.permisosSeleccionados.includes(id));
    },
    isPadreParcial(padre) {
      const todos = this._getTodosPermisosDePadre(padre);
      if (todos.length === 0) return false;
      const n = todos.filter(id => this.permisosSeleccionados.includes(id)).length;
      return n > 0 && n < todos.length;
    },
    isHijoCompleto(hijo) {
      if (!hijo.permisos?.length) return false;
      return hijo.permisos.every(p => this.permisosSeleccionados.includes(p.idPermiso));
    },
    isHijoParcial(hijo) {
      if (!hijo.permisos?.length) return false;
      const n = hijo.permisos.filter(p => this.permisosSeleccionados.includes(p.idPermiso)).length;
      return n > 0 && n < hijo.permisos.length;
    },
    togglePadre(padre, event) {
      const marcar = event.target.checked;
      const ids = this._getTodosPermisosDePadre(padre);
      if (marcar) {
        ids.forEach(id => { if (!this.permisosSeleccionados.includes(id)) this.permisosSeleccionados.push(id); });
      } else {
        this.permisosSeleccionados = this.permisosSeleccionados.filter(id => !ids.includes(id));
      }
    },
    toggleHijo(hijo, event) {
      const marcar = event.target.checked;
      const ids = hijo.permisos.map(p => p.idPermiso);
      if (marcar) {
        ids.forEach(id => { if (!this.permisosSeleccionados.includes(id)) this.permisosSeleccionados.push(id); });
      } else {
        this.permisosSeleccionados = this.permisosSeleccionados.filter(id => !ids.includes(id));
      }
    },
    seleccionarTodo() { this.permisosSeleccionados = this.permisosDisponibles.map(p => p.idPermiso); },
    deseleccionarTodo() { this.permisosSeleccionados = []; },

    async savePermissionsAssignment() {
      if (!this.selectedRolId) return;
      this.savingAssignment = true;
      try {
        const response = await obtenerPermisosPorRolConEstado(this.selectedRolId);
        const permisosActuales = response.success
          ? (response.data || []).filter(p => p.asignado).map(p => p.idPermiso)
          : [];
        const permisosQuitar = permisosActuales.filter(id => !this.permisosSeleccionados.includes(id));
        const permisosAgregar = this.permisosSeleccionados.filter(id => !permisosActuales.includes(id));
        for (const idPermiso of permisosQuitar) await quitarPermisoDeRol({ idPermiso, idRol: this.selectedRolId });
        for (const idPermiso of permisosAgregar) await asignarPermisoARol({ idPermiso, idRol: this.selectedRolId });
        await this.loadRoles();
        this.toast.success('Permisos asignados correctamente');
        this.closeAssignPermissionsModal();
      } catch (error) {
        console.error('❌ Error guardando permisos:', error);
        this.toast.error('Error al guardar los permisos');
      } finally { this.savingAssignment = false; }
    },

    closeModal() { this.showModal = false; this.resetForm(); this.errors = {}; },
    closeDetailModal() { this.showDetailModal = false; this.selectedRol = null; },
    closeAssignPermissionsModal() {
      this.showAssignPermissionsModal = false;
      this.selectedRol = null;
      this.selectedRolId = null;
      this.permisosSeleccionados = [];
    },
    resetForm() { this.form = { idRol: null, nombre: '', descripcion: '', estado: 'activo' }; this.editingRol = null; },

    validateForm() {
      this.errors = {};
      if (!this.form.nombre.trim()) this.errors.nombre = 'El nombre es requerido';
      else if (this.form.nombre.length > 50) this.errors.nombre = 'El nombre no puede exceder 50 caracteres';
      else if (!/^[a-zA-ZáéíóúñÑ\s]+$/.test(this.form.nombre)) this.errors.nombre = 'Solo letras y espacios';
      if (this.form.descripcion && this.form.descripcion.length > 500) this.errors.descripcion = 'Máx 500 caracteres';
      return Object.keys(this.errors).length === 0;
    },

    async saveRol() {
      if (!this.validateForm()) return;
      this.saving = true;
      try {
        if (!this.editingRol) {
          const verifyResult = await verificarNombreRol(this.form.nombre);
          if (verifyResult.success && verifyResult.existe) {
            this.errors.nombre = 'Este nombre ya está registrado';
            this.saving = false;
            return;
          }
        }
        const rolData = { nombre: this.form.nombre.trim(), descripcion: this.form.descripcion.trim() || null, estado: this.form.estado || 'activo' };
        const result = this.editingRol ? await actualizarRol(this.editingRol.idRol, rolData) : await crearRol(rolData);
        if (result.success) {
          await this.loadRoles();
          this.closeModal();
          this.toast.success(this.editingRol ? 'Rol actualizado' : 'Rol creado');
        } else throw new Error(result.error || 'Error al guardar');
      } catch (error) {
        console.error('❌ Error al guardar rol:', error);
        this.toast.error(error.message || 'Error al guardar');
      } finally { this.saving = false; }
    },

    async confirmDelete(rol) {
      if (rol.cantidadUsuarios > 0) {
        this.toast.warning(`No se puede eliminar: tiene ${rol.cantidadUsuarios} usuario(s)`);
        return;
      }
      const confirmed = await this.showConfirm({
        title: '¿Eliminar rol?',
        message: `¿Estás seguro de eliminar el rol <strong>"${rol.nombre}"</strong>?`,
        detail: 'Esta acción no se puede deshacer.',
        confirmText: 'Sí, eliminar', cancelText: 'Cancelar', type: 'danger'
      });
      if (confirmed) await this.deleteRol(rol.idRol, rol.nombre);
    },

    async deleteRol(id, nombre) {
      try {
        const result = await eliminarRol(id);
        if (result.success) {
          await this.loadRoles();
          this.toast.success(`El rol "${nombre}" ha sido eliminado`);
        } else throw new Error(result.error || 'Error al eliminar');
      } catch (error) {
        console.error('❌ Error al eliminar rol:', error);
        this.toast.error(error.message || 'Error al eliminar');
      }
    },

    async toggleStatus(rol) {
      const esActivo = rol.estado?.toLowerCase() === 'activo';
      let mensaje = '';
      if (esActivo && rol.cantidadUsuarios > 0) mensaje = ` Este rol tiene ${rol.cantidadUsuarios} usuario(s).`;
      const confirmed = await this.showConfirm({
        title: `${!esActivo ? 'Activar' : 'Desactivar'} rol`,
        message: `¿Deseas ${!esActivo ? 'activar' : 'desactivar'} el rol <strong>"${rol.nombre}"</strong>?${mensaje}`,
        confirmText: `Sí, ${!esActivo ? 'activar' : 'desactivar'}`,
        cancelText: 'Cancelar', type: 'warning'
      });
      if (confirmed) {
        try {
          const result = await cambiarEstadoRol(rol.idRol, !esActivo);
          if (result.success) {
            await this.loadRoles();
            this.toast.success(`El rol ha sido ${!esActivo ? 'activado' : 'desactivado'}`);
          } else throw new Error(result.error || 'Error al cambiar estado');
        } catch (error) {
          console.error('❌ Error al cambiar estado:', error);
          this.toast.error('Error al cambiar el estado');
        }
      }
    },

    async exportToExcel() {
      if (!this.filteredRoles.length) return this.toast.warning('No hay datos');
      try {
        const result = exportarExcel(this.filteredRoles, 'roles');
        if (result.success) this.toast.success(result.message);
        else throw new Error(result.error);
        this.showExportMenu = false;
      } catch (error) { this.toast.error('Error: ' + error.message); }
    },
    async exportToPDF() {
      if (!this.filteredRoles.length) return this.toast.warning('No hay datos');
      try {
        const result = exportarPDF(this.filteredRoles, 'Reporte de Roles');
        if (result.success) this.toast.success(result.message);
        else throw new Error(result.error);
        this.showExportMenu = false;
      } catch (error) { this.toast.error('Error: ' + error.message); }
    },
    async exportFullReport() {
      if (!this.filteredRoles.length) return this.toast.warning('No hay datos');
      try {
        const result = await exportarReporteCompleto(this.filteredRoles, 'reporte_completo_roles');
        if (result.success) this.toast.success('Reporte completo generado');
        else throw new Error(result.error);
        this.showExportMenu = false;
      } catch (error) { this.toast.error('Error: ' + error.message); }
    },

    async verPermisosDelRol(idRol) {
      try {
        const response = await obtenerPermisosPorRolConEstado(idRol);
        if (response.success) {
          const permisosAsignados = (response.data || []).filter(p => p.asignado);
          const lista = permisosAsignados.map(p => `• ${p.nombre_accion} (${p.nombreModulo})`).join('\n');
          this.toast.info(`Permisos del rol:\n${lista || 'Ninguno'}`, { timeout: 8000 });
        }
      } catch (error) {
        console.error('Error:', error);
      }
    },

    clearFilters() {
      this.searchQuery = '';
      this.filterStatus = 'all';
      this.sortBy = 'nombre';
      this.currentPage = 1;
      this.loadRoles();
    },

    formatDate(fechaISO) {
      if (!fechaISO) return '';
      try { return new Date(fechaISO).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' }); }
      catch { return fechaISO; }
    },
    formatTime(fechaISO) {
      if (!fechaISO) return '';
      try { return new Date(fechaISO).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }); }
      catch { return ''; }
    },
  },
};
</script>

<style scoped>
@keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
.fade-in { animation: fadeIn 0.3s ease-out; }
.line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
.line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
::-webkit-scrollbar-thumb { background: #888; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #555; }
.dark ::-webkit-scrollbar-track { background: #374151; }
.dark ::-webkit-scrollbar-thumb { background: #6b7280; }
.dark ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
</style>